import { type ControllerRenderProps, useController } from 'react-hook-form';
import type {
  InviteViaEmailFormReturn,
  InviteViaEmailFormValues,
} from '../inviteViaEmailForm.types';
import { useEffect } from 'react';
import { storage } from '@doxyme/ui/utils';

export const getEmailInviteLocalStorageKey = (locale: string): string =>
  `account.dashboard.invite.email.${locale}`;

export const useInvitationField = (
  form: InviteViaEmailFormReturn,
  maxLength: number,
  /**
   * Default value is set based on selected locale.
   */
  defaultValue: string,
): ControllerRenderProps<InviteViaEmailFormValues, 'invitationField'> => {
  const [currentInvitationFieldValue] = form.watch(['invitationField']);

  const invitationField = useController({
    name: 'invitationField',
    defaultValue,
    rules: {
      maxLength,
    },
    control: form.control,
  }).field;

  useEffect(() => {
    /**
     * When default value changes we need to reset it.
     */
    if (!defaultValue) {
      return;
    }
    form.resetField('invitationField', { defaultValue, keepDirty: false });

    /**
     * NOTE, it is not ideal solution as we actually applying logic based on default value change not
     * language change.
     * Now it works correctly as default value changes when language changes.
     *
     * To apply logic based on both of them would lead to inconsistency (override each other).
     * It could be fixed by using probably a separate variable in state but really do not like that idea for now.
     */
    const currentLanguage = form.getValues('languageField');
    /**
     *  If there's a local storage value for the given locale - let's use it to set the input value and trigger revalidation.
     */
    const localStorageValue: string | undefined = storage.getItem(
      getEmailInviteLocalStorageKey(currentLanguage),
      true,
    );

    if (
      form.getFieldState('invitationField').isDirty ||
      localStorageValue === undefined
    ) {
      return;
    }

    form.setValue('invitationField', localStorageValue, { shouldDirty: true });
    void form.trigger('invitationField');
  }, [defaultValue]);

  // Persist the text to local storage in real time, but only if it's not the default value.
  useEffect(() => {
    if (
      currentInvitationFieldValue === '' ||
      currentInvitationFieldValue === undefined ||
      !form.getValues('languageField')
    ) {
      return;
    }
    const currentLanguage = form.getValues('languageField');

    if (
      currentInvitationFieldValue !== defaultValue &&
      form.getFieldState('invitationField').isDirty
    ) {
      storage.setItem(
        getEmailInviteLocalStorageKey(currentLanguage),
        currentInvitationFieldValue,
        true,
      );
    } else {
      storage.removeItem(getEmailInviteLocalStorageKey(currentLanguage), true);
    }
  }, [currentInvitationFieldValue, defaultValue]);

  return invitationField;
};
